// Auto-assembling elevation — each added member is drawn at its position (left post, top rail, slats…).
export const MembersPreview = ({ members = [], layout = {}, colourHex = "#334155" }) => {
  const W = 420, H = 240;
  const ms = members.filter((m) => (m.length_mm || 0) > 0 && (m.qty || 0) > 0);
  const widthMm = layout.width_mm || 2400, heightMm = layout.height_mm || 1800;
  const scale = Math.min((W - 110) / widthMm, (H - 70) / heightMm);
  const pw = widthMm * scale, ph = heightMm * scale;
  const x0 = 65, y0 = 30;
  const expand = (list) => list.flatMap((m) => Array.from({ length: parseInt(m.qty) || 1 }, () => m));

  const leftPosts = expand(ms.filter((m) => m.kind === "post" && m.position === "left"));
  const rightPosts = expand(ms.filter((m) => m.kind === "post" && m.position === "right"));
  const centrePosts = expand(ms.filter((m) => m.kind === "post" && (!m.position || m.position === "centre")));
  const topRails = expand(ms.filter((m) => m.kind === "rail" && m.position === "top"));
  const botRails = expand(ms.filter((m) => m.kind === "rail" && m.position === "bottom"));
  const midRails = expand(ms.filter((m) => m.kind === "rail" && (!m.position || m.position === "mid")));
  const hSlats = expand(ms.filter((m) => m.kind === "slat" && m.position !== "vertical"));
  const vSlats = expand(ms.filter((m) => m.kind === "slat" && m.position === "vertical"));
  const tubes = expand(ms.filter((m) => m.kind === "tube"));
  const braces = expand(ms.filter((m) => m.kind === "brace"));
  const louvres = expand(ms.filter((m) => m.kind === "louvre"));
  const hBlades = expand(ms.filter((m) => m.kind === "blade" && m.position === "horizontal"));
  const vBlades = expand(ms.filter((m) => m.kind === "blade" && m.position !== "horizontal"));
  const laserSheets = ms.filter((m) => m.kind === "laser_cut");

  const els = [];
  const vBar = (key, x, m, wpx) => {
    const len = Math.min(m.length_mm, heightMm) * scale;
    els.push(<rect key={key} x={x} y={y0 + ph - len} width={wpx} height={len} fill={colourHex} />);
  };
  const hBar = (key, y, m, hpx) => {
    const len = Math.min(m.length_mm, widthMm) * scale;
    els.push(<rect key={key} x={x0} y={y} width={len} height={hpx} fill={colourHex} />);
  };
  const postW = Math.max(2.5, 50 * scale * 0.6), railH = Math.max(2, 40 * scale * 0.55);

  // Laser-cut sheet sits behind everything else — draw first
  laserSheets.forEach((m, i) => {
    els.push(
      <g key={`lc${i}`}>
        <rect x={x0 + postW} y={y0 + railH} width={pw - 2 * postW} height={ph - 2 * railH} fill="#f8fafc" stroke={colourHex} strokeWidth={0.7} />
        <text x={x0 + pw / 2} y={y0 + ph / 2} fontSize={10} fill={colourHex} textAnchor="middle" fontStyle="italic">
          Laser-cut{m.note ? ` — ${m.note.slice(0, 20)}` : ""}
        </text>
      </g>
    );
  });

  leftPosts.forEach((m, i) => vBar(`lp${i}`, x0 + i * (postW + 2), m, postW));
  rightPosts.forEach((m, i) => vBar(`rp${i}`, x0 + pw - postW - i * (postW + 2), m, postW));
  centrePosts.forEach((m, i) => vBar(`cp${i}`, x0 + postW + ((i + 1) * (pw - 2 * postW)) / (centrePosts.length + 1), m, postW));
  topRails.forEach((m, i) => hBar(`tr${i}`, y0 + i * (railH + 2), m, railH));
  botRails.forEach((m, i) => hBar(`br${i}`, y0 + ph - railH - i * (railH + 2), m, railH));
  midRails.forEach((m, i) => hBar(`mr${i}`, y0 + ((i + 1) * ph) / (midRails.length + 1) - railH / 2, m, railH));

  // Draw horizontal members honouring gap_mm when > 0.
  const drawHStack = (keyPrefix, items, defaultThicknessFactor) => {
    if (!items.length) return;
    const first = items[0];
    const memberW = Math.max(1.2, (first.member_width_mm || 0) * scale);
    const gap = (first.gap_mm || 0) * scale;
    const usableH = ph - 2 * railH - 4;
    if ((first.gap_mm || 0) > 0 && memberW > 0) {
      const pitch = memberW + gap;
      items.forEach((m, i) => {
        const topY = y0 + railH + 2 + i * pitch;
        if (topY + memberW > y0 + ph - railH - 2) return;
        hBar(`${keyPrefix}${i}`, topY, m, memberW);
      });
    } else {
      const n = Math.max(1, items.length);
      items.forEach((m, i) => hBar(`${keyPrefix}${i}`, y0 + railH + 2 + (i * usableH) / n, m, Math.max(1.2, (usableH / n) * defaultThicknessFactor)));
    }
  };

  const drawVStack = (keyPrefix, items, defaultThicknessFactor) => {
    if (!items.length) return;
    const first = items[0];
    const memberW = Math.max(1.0, (first.member_width_mm || 0) * scale);
    const gap = (first.gap_mm || 0) * scale;
    const usableW = pw - 2 * postW - 4;
    if ((first.gap_mm || 0) > 0 && memberW > 0) {
      const pitch = memberW + gap;
      items.forEach((m, i) => {
        const leftX = x0 + postW + 2 + i * pitch;
        if (leftX + memberW > x0 + pw - postW - 2) return;
        vBar(`${keyPrefix}${i}`, leftX, m, memberW);
      });
    } else {
      const n = Math.max(1, items.length);
      items.forEach((m, i) => vBar(`${keyPrefix}${i}`, x0 + postW + 2 + (i * usableW) / n, m, Math.max(1.0, (usableW / n) * defaultThicknessFactor)));
    }
  };

  drawHStack("hs", hSlats, 0.7);
  drawVStack("vs", vSlats, 0.7);
  drawVStack("tu", tubes, 0.35);
  drawHStack("lv", louvres, 0.65);
  drawHStack("bh", hBlades, 0.4);
  drawVStack("bv", vBlades, 0.4);
  braces.forEach((m, i) => els.push(<line key={`b${i}`} x1={x0} y1={y0 + ph} x2={x0 + pw * 0.9} y2={y0 + ph * 0.1} stroke={colourHex} strokeWidth={3} strokeDasharray={i % 2 ? "6 3" : undefined} />));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-h-72 bg-white rounded-sm border border-slate-200" data-testid="members-preview">
      {ms.length === 0 && <text x={W / 2} y={H / 2} fontSize={11} fill="#94a3b8" textAnchor="middle">{"Add members below — each one is drawn into the blueprint at its position"}</text>}
      {els}
      {ms.length > 0 && (
        <>
          <line x1={x0} y1={y0 + ph + 12} x2={x0 + pw} y2={y0 + ph + 12} stroke="#0e7490" strokeWidth={1} />
          <text x={x0 + pw / 2} y={y0 + ph + 24} fontSize={10} fontFamily="monospace" fill="#0e7490" textAnchor="middle" fontWeight="bold">{`${widthMm} mm`}</text>
          <line x1={x0 - 12} y1={y0} x2={x0 - 12} y2={y0 + ph} stroke="#0e7490" strokeWidth={1} />
          <text x={x0 - 18} y={y0 + ph / 2} fontSize={10} fontFamily="monospace" fill="#0e7490" textAnchor="middle" fontWeight="bold" transform={`rotate(-90 ${x0 - 18} ${y0 + ph / 2})`}>{`${heightMm} mm`}</text>
        </>
      )}
    </svg>
  );
};

export default MembersPreview;
